import { Component, Injectable, signal, WritableSignal } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { UIContextAction } from 'angular-to-phaser';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { ShadowPopoverDirective } from '../directives/shadow-popover.directive';
import { DirectActionReply, Interaction, InteractionReply } from '../dto';
import { AbstractService } from './abstractservice';
import { AuthService } from './auth.service';
import { FeatureFlagService } from './feature-flag.service';
import { WorldService } from './world.service';

@Injectable({
    providedIn: 'root'
})
export class TutorialService extends AbstractService {

    private SetupWizardNpcId = "setupWizard";
    // kitTaken/tutorialPostponed are read from AppComponent's OnPush template
    // (app.component.html) - both are written from async call sites (worldService
    // responses, not this component's own template events), so they need to be signals for
    // that read to stay live. tutorialComplete/alreadyTutorialPrompted have no template
    // reader anywhere in the app and are left as plain fields.
    public kitTaken: WritableSignal<boolean> = signal<boolean>(false);
    public tutorialComplete = false;
    public alreadyTutorialPrompted = false;
    public tutorialPostponed: WritableSignal<boolean> = signal<boolean>(false);
    // No external consumers app-wide (verified by grep) - a plain WritableSignal, no
    // toObservable() bridge needed.
    public tutorialIsLoaded: WritableSignal<boolean> = signal<boolean>(false);

    private tutorialComponentType: Component = null;

    tutorialOptions: InteractionReply[];
    appShellTutorialSections: ShadowPopoverDirective[] = [];
    tutorialSections: ShadowPopoverDirective[] = [];
    wizardMessage: Interaction;
    currentTutorialStep = 0;

    constructor(private modalService: NgbModal, authService: AuthService, featureFlagService: FeatureFlagService, private worldService: WorldService) {
        super(authService, featureFlagService);
    }

    public resetTutorialState() {
        this.currentTutorialStep = 0;
        this.determineTutorialCompleteAndSetWizardState().subscribe();
    }

    public showTutorial() {
        if ((!this.authService.sessionSummary?.TutorialComplete && !this.tutorialComplete) || !this.kitTaken()) {
            this.determineTutorialCompleteAndSetWizardState().subscribe(noTutorialRemaining => {
                if (!noTutorialRemaining) {
                    this.modalService.open(this.tutorialComponentType);
                }
            });
        }
    }

    public checkShowTutorial() {
        if (!this.authService.sessionSummary?.TutorialComplete && this.tutorialIsLoaded() && this.authService.isAuthenticated() && !this.alreadyTutorialPrompted && !this.tutorialComplete) {
            this.alreadyTutorialPrompted = true;
            this.showTutorial();
        }
    }

    public addTutorialComponent(tutorialComponentType: any) {
        this.tutorialComponentType = tutorialComponentType;
        this.tutorialIsLoaded.set(this.tutorialComponentType !== null && this.tutorialSections.find(appShellElement => appShellElement.appShadowPopover === 'tutorial1') !== undefined);
        this.checkShowTutorial();
    }

    public addAppShellTutorialSection(pops: ShadowPopoverDirective[]) {
        this.appShellTutorialSections.push(...pops);
        this.tutorialIsLoaded.set(this.tutorialComponentType !== null && this.tutorialSections.find(appShellElement => appShellElement.appShadowPopover === 'tutorial1') !== undefined);
        this.checkShowTutorial();
    }

    public addTutorialSections(pops: ShadowPopoverDirective[]) {
        this.tutorialSections.push(...pops);
        this.tutorialIsLoaded.set(this.tutorialComponentType !== null && this.tutorialSections.find(appShellElement => appShellElement.appShadowPopover === 'tutorial1') !== undefined);
        this.checkShowTutorial();
    }
    public removeTutorialSection(section: ShadowPopoverDirective) {
        this.appShellTutorialSections = this.appShellTutorialSections.filter(s => s !== section);
        this.tutorialSections = this.tutorialSections.filter(s => s !== section);
    }

    public determineTutorialCompleteAndSetWizardState(): Observable<boolean> {
        if (this.authService.sessionSummary?.TutorialComplete) {
            this.kitTaken.set(true);
            this.tutorialComplete = true;
            return of(this.kitTaken() && this.tutorialComplete);
        }
        return this.worldService.retrieveNpcOptions(this.SetupWizardNpcId)
            .pipe(
                switchMap(interaction => {
                    if (interaction) {
                        this.wizardMessage = interaction;
                    }
                    this.tutorialOptions = interaction.Responses;
                    if (interaction.Responses?.length === 0 ||
                        (interaction?.Responses.length === 1 &&
                            (!interaction.Responses[0].Actions?.find(a => a instanceof DirectActionReply) ||
                                interaction.Responses[0].DisplayMetadata.IsFinalOption))) {

                        // Only the "Okay"/GiftBasketPending screen means the kit is still
                        // unclaimed - "Thanks!"/KitReceived (UIContextAction.Dismiss) is reached
                        // AFTER the grant already ran, same as the fully silent AllDone (empty
                        // Responses) case below.
                        let actionWithCode = interaction.Responses[0]?.UIContextAction !== UIContextAction.Dismiss
                            ? interaction.Responses[0]?.Actions?.find(a => a instanceof DirectActionReply)
                            : null;

                        this.kitTaken.set((interaction.Responses?.length === 0) || !actionWithCode);
                        this.tutorialComplete = true;
                        this.tutorialPostponed.set(false);
                    } else {
                        this.tutorialComplete = false;
                        this.kitTaken.set(false);
                        this.processInteraction(interaction);
                    }

                    return of(this.kitTaken() && this.tutorialComplete);
                }));
    }

    // Dispatches whatever DirectAction the clicked reply itself carries (ExternalId/ActionCode/
    // RecipientType) rather than a hardcoded NPC action code - SetupWizard is now dialogue-
    // driven, so a "reply" addresses a specific quest+StepReply (RecipientType.QuestReply,
    // ExternalId = the quest instance id, ActionCode = that Step's ReplyId), not a fixed,
    // NPC-wide action code scheme. See Postgres/seed/features/setupwizard-dialogue.sql for the
    // server-side content this now round-trips against.
    public submitTutorialChoice(reply: InteractionReply): Observable<Interaction> {
        const directAction = (reply.Actions.find(a => a instanceof DirectActionReply) as DirectActionReply).DirectAction;
        return this.worldService.submitAction(directAction.ExternalId, directAction.ActionCode, directAction.RecipientType)
            .pipe(
                tap(interaction => {
                    this.processInteraction(interaction);
                    // The postponed-acknowledgement screen ("Okay! Come back when you have
                    // time...") is the only single-reply response reachable from a real (non-
                    // chained) submission that's both IsFinalOption and UIContextAction.Dismiss -
                    // "Thanks!"/"Okay" (kit) never reach this tap at all, since they're chained
                    // locally via advanceToSubsequentAction instead.
                    if (interaction.Responses?.length === 1 &&
                        interaction.Responses[0].DisplayMetadata.IsFinalOption &&
                        interaction.Responses[0].UIContextAction === UIContextAction.Dismiss) {
                        this.tutorialPostponed.set(true);
                    }
                    this.currentTutorialStep = interaction.ContextSpecificData.CurrentStep;
                }));
    }

    // Takes the actually-clicked reply rather than re-deriving it from this.wizardMessage:
    // IsFinalOption replies (e.g. "Okay") call resetTutorialState() right after submitting,
    // which kicks off its own background re-greet - if that resolves before the user clicks
    // the just-rendered chained "Thanks!" reply, it silently overwrites this.wizardMessage/
    // tutorialOptions out from under the still-displayed button, and re-deriving from the
    // (now different) this.wizardMessage.Responses either finds the wrong reply or - since a
    // silent terminal response has zero Responses - throws trying to call .Actions on
    // undefined. The clicked reply's own NextEvent is always correct regardless of that race.
    public advanceToSubsequentAction(clickedReply: InteractionReply) {
        this.wizardMessage = clickedReply.Actions.find(a => a.NextEvent).NextEvent;
        this.tutorialOptions = this.wizardMessage.Responses;
    }

    private processInteraction(interaction: Interaction) {
        // Both branches used to check the exact same condition, so a response with zero
        // Responses (the tutorial's own terminal "AllDone" Step, reached right after "Take a
        // starter kit") never cleared the previous options - the stale, already-consumed reply
        // button stayed visible (and clickable, though re-submitting it would fail server-side)
        // forever. Only surfaced once the modal stopped auto-closing on that same reply (see
        // TutorialComponent.chooseOption's own comment) - previously nothing lingered long
        // enough on screen to notice.
        this.tutorialOptions = interaction.Responses ?? [];
        this.currentTutorialStep = interaction.ContextSpecificData.CurrentStep;
        this.wizardMessage = interaction ?? this.wizardMessage;

    }
}
